/**
 * +quests: pure model of the side quests for the world HUD (docs/design/zones.md "Side quests"):
 * the over-head markers of the quest NPCs, what an NPC's dialog offers, the tracker rows under the
 * main quest, and where the waypoint arrow points for a tracked side quest. No DOM.
 */
import { type SideQuestDef, SIDE_QUESTS, sideQuestById, sideQuestTurnInId } from '@keystone/content';
import { type HeroCharacter, type SideQuestStatus, sideQuestGrey, sideQuestStatus, sideQuestXp, sideQuestsOf } from '@keystone/progression';
import type { NpcMarker } from '../render/types';
import type { TrackerRow, Waypoint } from './fieldView';

type Hero = Pick<HeroCharacter, 'quests' | 'bounties' | 'zoneId' | 'mapId'>;

export interface QuestNames {
  npc(id: string): string | undefined;
  area(id: string): string | undefined;
}

/** NPCs that give or take side quests. */
const QUEST_NPC_IDS: readonly string[] = [...new Set(SIDE_QUESTS.flatMap((q) => [q.giverId, sideQuestTurnInId(q)]))];

/**
 * Markers over the quest NPCs: '?' (gold) a quest is ready to hand in here; '!' (gold) one is
 * available; grey '!' the next one unlocks at a higher level; grey '?' one of theirs is in progress.
 */
export function questNpcMarkers(ch: Hero | null, level: number): Record<string, NpcMarker | null> {
  const out: Record<string, NpcMarker | null> = {};
  for (const id of QUEST_NPC_IDS) out[id] = null;
  if (!ch) return out;
  const rank: Record<NpcMarker, number> = { turnin: 4, available: 3, locked: 2, progress: 1 };
  const put = (id: string, m: NpcMarker): void => {
    const cur = out[id];
    if (!cur || rank[m] > rank[cur]) out[id] = m;
  };
  for (const q of SIDE_QUESTS) {
    const st = sideQuestStatus(ch, q, level);
    if (st === 'ready') put(sideQuestTurnInId(q), 'turnin');
    else if (st === 'available') put(q.giverId, 'available');
    else if (st === 'locked') put(q.giverId, 'locked');
    else if (st === 'active') put(q.giverId, 'progress');
  }
  return out;
}

/** One line of an NPC's quest menu. */
export interface QuestMenuEntry {
  def: SideQuestDef;
  status: SideQuestStatus;
  /** What this NPC does with it: take it in, offer it, nag about it, or tease it. */
  role: 'turnin' | 'offer' | 'progress' | 'locked';
  grey: boolean;
  /** XP it pays this hero now (outgrown quests pay less). */
  xp: number;
}

/** What `npcId` has for this hero, in dialog order (hand-ins, offers, in progress, locked). */
export function npcQuestMenu(ch: Hero | null, level: number, npcId: string): QuestMenuEntry[] {
  if (!ch) return [];
  const out: QuestMenuEntry[] = [];
  for (const def of SIDE_QUESTS) {
    const st = sideQuestStatus(ch, def, level);
    const mine = def.giverId === npcId;
    const takes = sideQuestTurnInId(def) === npcId;
    let role: QuestMenuEntry['role'] | null = null;
    if (st === 'ready' && takes) role = 'turnin';
    else if (st === 'available' && mine) role = 'offer';
    else if ((st === 'active' || st === 'ready') && mine) role = 'progress';
    else if (st === 'locked' && mine) role = 'locked';
    if (role) out.push({ def, status: st, role, grey: sideQuestGrey(def.level, level), xp: sideQuestXp(def.reward.xp, def.level, level) });
  }
  const order = { turnin: 0, offer: 1, progress: 2, locked: 3 } as const;
  return out.sort((a, b) => order[a.role] - order[b.role]);
}

/** "Damp Socks: 3/5" / "Meadow Slimes: 3/8" / the talk objective. */
export function questProgressText(def: SideQuestDef, have: number, names: QuestNames): string {
  if (def.kind === 'talk') return `Speak with ${names.npc(sideQuestTurnInId(def)) ?? 'them'}`;
  const what = def.kind === 'collect' ? def.item!.plural : mobList(def.npcIds, names);
  return `${what}: ${Math.min(have, def.count)}/${def.count}`;
}

function mobList(ids: readonly string[], names: QuestNames): string {
  const n = ids.map((id) => plural(names.npc(id) ?? id));
  return n.length <= 1 ? (n[0] ?? '') : `${n.slice(0, -1).join(', ')} or ${n[n.length - 1]}`;
}
const plural = (s: string): string => (/(s|sh|ch)$/i.test(s) ? `${s}es` : /[^aeiou]y$/i.test(s) ? `${s.slice(0, -1)}ies` : s.startsWith('The ') ? s : `${s}s`);

/**
 * Tracker rows for the accepted side quests, in the order they were taken (under the main quest
 * and the bounties). `tracked` marks the one the arrow follows.
 */
export function sideQuestRows(ch: Hero | null, level: number, names: QuestNames, tracked: string | null): TrackerRow[] {
  if (!ch) return [];
  const q = sideQuestsOf(ch);
  const rows: TrackerRow[] = [];
  for (const id of Object.keys(q.active)) {
    const def = sideQuestById(id);
    if (!def) continue;
    const have = q.active[id] ?? 0;
    const st = sideQuestStatus(ch, def, level);
    const done = st === 'ready';
    const talk = def.kind === 'talk';
    const turnIn = names.npc(sideQuestTurnInId(def)) ?? 'the quest giver';
    rows.push({
      id,
      kind: 'quest',
      title: def.title,
      text: talk ? `Speak with ${turnIn}` : def.kind === 'collect' ? def.item!.plural : mobList(def.npcIds, names),
      cur: talk ? 0 : Math.min(have, def.count),
      max: talk ? 1 : def.count,
      done: done && !talk,
      ...(done && !talk ? { hint: `Return to ${turnIn}` } : {}),
      ...(tracked === id ? { tracked: true } : {}),
      ...(sideQuestGrey(def.level, level) ? { grey: true } : {}),
    });
  }
  return rows;
}

/** Where an NPC stands on `mapId` (the caller's lookup). */
export type NpcPlace = (npcId: string) => { mapId: string; pos: { x: number; y: number } } | null;

/**
 * The waypoint for side quest `id` on `mapId`: its turn-in NPC once it is ready (and for talk
 * quests), else its hunting ground. Null when it is not active or is on another map.
 */
export function sideQuestWaypoint(ch: Hero | null, level: number, id: string, mapId: string, place: NpcPlace, names: QuestNames): Waypoint | null {
  if (!ch) return null;
  const def = sideQuestById(id);
  if (!def || def.mapId !== mapId) return null;
  const st = sideQuestStatus(ch, def, level);
  if (st !== 'active' && st !== 'ready') return null;
  if (st === 'ready') {
    const at = place(sideQuestTurnInId(def));
    if (!at || at.mapId !== mapId) return null;
    return { pos: { ...at.pos }, label: `${def.title}: ${names.npc(sideQuestTurnInId(def)) ?? 'turn in'}`, portal: false };
  }
  const spot = def.huntAt ?? place(def.giverId)?.pos;
  return spot ? { pos: { ...spot }, label: def.title, portal: false } : null;
}

/** The first active side quest (the arrow's fallback when the main quest points nowhere). */
export function firstActiveQuest(ch: Hero | null): string | null {
  if (!ch) return null;
  return Object.keys(sideQuestsOf(ch).active)[0] ?? null;
}
